import { useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { changePassword } from '../api/auth';
import { extractErrorMessage } from '../api/errors';

/**
 * Profile-page "Change password" section.
 *
 * The signed-in path to rotating a password — the forgot-password flow
 * needs a working mailbox, which isn't always available (e.g. SES
 * sandbox). Requires the current password (re-verified server-side);
 * the new one is checked client-side for match + the server enforces
 * the complexity policy.
 */
export function ChangePasswordSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (next !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(current, next);
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(extractErrorMessage(err, 'could not change password'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Change password
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Current password"
              type="password"
              fullWidth
              required
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              disabled={submitting}
            />
            <TextField
              label="New password"
              type="password"
              fullWidth
              required
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              disabled={submitting}
            />
            <TextField
              label="Confirm new password"
              type="password"
              fullWidth
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={submitting}
            />
            {error && <Alert severity="error">{error}</Alert>}
            {success && (
              <Alert severity="success">Password changed.</Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !current || !next || !confirm}
            >
              {submitting ? 'Changing…' : 'Change password'}
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
